import React, { useState, useEffect } from 'react';
import api, { certificatesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { MagnifyingGlass, User, Briefcase, Phone, Envelope, CalendarBlank, CheckCircle, XCircle, Clock, Warning, Eye, Certificate, Download } from '@phosphor-icons/react';
import { toast } from 'sonner';
import CourseCountBadge from './CourseCountBadge';

export default function WorkerRutSearch() {
  const { isAdmin } = useAuth();
  const [rut, setRut] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [generatingId, setGeneratingId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewLabel, setPreviewLabel] = useState('');
  const [previewCertId, setPreviewCertId] = useState('');

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const handleView = async (certId, label) => {
    if (!certId) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl('');
    setPreviewLabel(label || '');
    setPreviewCertId(certId);
    try {
      const res = await certificatesApi.preview(certId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch {
      toast.error('No se pudo cargar el certificado');
      setPreviewOpen(false);
    } finally { setPreviewLoading(false); }
  };

  const handleDownload = async () => {
    if (!previewCertId) return;
    try {
      const res = await certificatesApi.download(previewCertId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'certificado.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo descargar el certificado'); }
  };

  const handleGenerate = async (t) => {
    setGeneratingId(t.enrollment_id);
    try {
      const res = await certificatesApi.generate({ enrollment_id: t.enrollment_id });
      const d = res.data || {};
      setResult((prev) => ({
        ...prev,
        trainings: prev.trainings.map((it) => (
          it.enrollment_id === t.enrollment_id
            ? { ...it, certificate_id: d.id || it.certificate_id, certificate_code: d.code || it.certificate_code,
                certificate_expiry: d.expiry_date || it.certificate_expiry, cert_estado: d.cert_estado || 'Vigente',
                can_generate_certificate: false }
            : it
        )),
      }));
      toast.success(d.already_existed ? 'El certificado ya existía' : `Certificado generado: ${d.code}`);
      if (d.id) handleView(d.id, `${result.worker.first_name} ${result.worker.last_name} — ${d.code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar el certificado');
    } finally { setGeneratingId(''); }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!rut.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get(`/users/search-rut/${encodeURIComponent(rut.trim())}`);
      setResult(res.data);
    } catch (err) {
      setResult(null);
      toast.error(err.response?.data?.detail || 'No se encontró el trabajador');
    } finally { setLoading(false); }
  };

  const getResultBadge = (t) => {
    if (t.score === null || t.score === undefined) {
      return <Badge className="bg-gray-100 text-gray-500 border-gray-200 border"><Clock size={12} className="mr-1" /> Pendiente</Badge>;
    }
    if (t.score >= 60) {
      return <Badge className="bg-green-50 text-green-600 border-green-200 border"><CheckCircle size={12} className="mr-1" /> Aprobado ({t.score}%)</Badge>;
    }
    return <Badge className="bg-red-50 text-red-600 border-red-200 border"><XCircle size={12} className="mr-1" /> Reprobado ({t.score}%)</Badge>;
  };

  const getStatusBadge = (status) => {
    const c = { sin_conformar: 'bg-yellow-50 text-yellow-600 border-yellow-200', conformada: 'bg-blue-50 text-blue-600 border-blue-200', realizada: 'bg-green-50 text-green-600 border-green-200' };
    const l = { sin_conformar: 'Sin Conformar', conformada: 'Conformada', realizada: 'Realizada' };
    return <Badge className={`${c[status] || 'bg-gray-100 text-gray-500 border-gray-200'} border`}>{l[status] || status}</Badge>;
  };

  return (
    <Card className="bg-white border-[#2B4B8C]/10" data-testid="rut-search-card">
      <CardContent className="p-5 space-y-4">
        <h3 className="text-base font-semibold text-[#2B4B8C]">Consulta por RUT</h3>
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/40" size={18} />
            <Input
              placeholder="Ej: 12.345.678-9"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]"
              data-testid="rut-search-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="bg-[#2BBAA4] text-black hover:bg-[#239986]" data-testid="rut-search-btn">
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
        </form>

        {searched && result && (
          <div className="space-y-4 animate-fade-in">
            {/* Worker Info */}
            <div className="rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/10 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2B4B8C]/10 flex items-center justify-center shrink-0">
                  <User size={20} className="text-[#2B4B8C]" />
                </div>
                <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2">
                  <div>
                    <p className="text-[10px] uppercase text-[#2B4B8C]/40">Nombre</p>
                    <p className="text-sm font-semibold text-[#2B4B8C]">{result.worker.first_name} {result.worker.last_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#2B4B8C]/40">RUT</p>
                    <p className="text-sm text-[#2B4B8C]">{result.worker.rut}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase text-[#2B4B8C]/40">Empresa</p>
                    <p className="text-sm text-[#2B4B8C]">{result.worker.company_name || '-'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Briefcase size={12} className="text-[#2B4B8C]/40" />
                    <p className="text-sm text-[#2B4B8C]/70">{result.worker.cargo || '-'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Envelope size={12} className="text-[#2B4B8C]/40" />
                    <p className="text-sm text-[#2B4B8C]/70">{result.worker.email}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Phone size={12} className="text-[#2B4B8C]/40" />
                    <p className="text-sm text-[#2B4B8C]/70">{result.worker.phone || '-'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Alerts */}
            {result.course_alerts && result.course_alerts.length > 0 && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-1">
                {result.course_alerts.map((ca, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Warning size={14} className="text-red-500 shrink-0" />
                    <span className="text-xs text-red-600 font-medium">{ca.course_name}:</span>
                    <span className="text-xs text-red-500">{ca.alerts.join(' | ')}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Trainings */}
            <div>
              <h4 className="text-sm font-semibold text-[#2B4B8C] mb-2">
                Capacitaciones ({result.trainings.length})
              </h4>
              {result.trainings.length > 0 ? (
                <div className="rounded-lg border border-[#2B4B8C]/10 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Capacitación</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Curso</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Nº Vez</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Fecha</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Estado</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Asistencia</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Resultado</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Vigencia</TableHead>
                        <TableHead className="text-[#2B4B8C]/60 text-xs">Certificado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.trainings.map((t, i) => (
                        <TableRow key={i} className="border-[#2B4B8C]/5">
                          <TableCell className="text-[#2B4B8C] text-sm font-medium py-2">{t.training_name}</TableCell>
                          <TableCell className="text-[#2B4B8C]/60 text-sm py-2">{t.course_name || '-'}</TableCell>
                          <TableCell className="py-2"><CourseCountBadge count={t.course_count} /></TableCell>
                          <TableCell className="text-[#2B4B8C]/60 text-sm py-2">
                            {t.scheduled_date ? new Date(t.scheduled_date).toLocaleDateString('es-CL') : '-'}
                          </TableCell>
                          <TableCell className="py-2">{getStatusBadge(t.status)}</TableCell>
                          <TableCell className="py-2">
                            {t.status === 'realizada' ? (
                              t.attended ? (
                                <Badge className="bg-green-500/10 text-green-600 border-green-500/20 border text-[10px]">Asistió</Badge>
                              ) : (
                                <Badge className="bg-red-500/10 text-red-500 border-red-500/20 border text-[10px]">Ausente</Badge>
                              )
                            ) : (
                              <span className="text-[#2B4B8C]/30 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="py-2">{getResultBadge(t)}</TableCell>
                          <TableCell className="py-2">
                            {t.cert_estado === 'Vigente' ? (
                              <div className="flex flex-col">
                                <Badge className="bg-green-50 text-green-600 border-green-200 border text-[10px] w-fit">Vigente</Badge>
                                {t.certificate_expiry ? <span className="text-[10px] text-[#2B4B8C]/50 mt-0.5">Hasta {t.certificate_expiry}</span> : null}
                                {t.cert_remaining ? <span className="text-[10px] font-medium text-[#2BBAA4] mt-0.5" data-testid="cert-remaining">{t.cert_remaining}</span> : null}
                              </div>
                            ) : t.cert_estado === 'Vencido' ? (
                              <div className="flex flex-col">
                                <Badge className="bg-red-50 text-red-500 border-red-200 border text-[10px] w-fit">Vencido</Badge>
                                {t.certificate_expiry ? <span className="text-[10px] text-[#2B4B8C]/50 mt-0.5">Venció {t.certificate_expiry}</span> : null}
                                {t.cert_remaining ? <span className="text-[10px] font-medium text-red-500 mt-0.5" data-testid="cert-remaining">{t.cert_remaining}</span> : null}
                              </div>
                            ) : t.cert_remaining ? (
                              <span
                                className={`text-[10px] font-medium ${t.cert_remaining.startsWith('Vencido') ? 'text-red-500' : 'text-[#2BBAA4]'}`}
                                data-testid="cert-remaining"
                              >
                                {t.cert_remaining}
                              </span>
                            ) : (
                              <span className="text-[#2B4B8C]/30 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell className="py-2">
                            {t.certificate_id ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 border-[#2B4B8C]/20 text-[#2B4B8C] hover:bg-[#2BBAA4]/10 text-xs font-normal"
                                onClick={() => handleView(t.certificate_id, `${result.worker.first_name} ${result.worker.last_name} — ${t.certificate_code}`)}
                                data-testid={`rut-view-cert-${t.enrollment_id}`}>
                                <Eye size={14} className="mr-1" /> Ver
                              </Button>
                            ) : (t.can_generate_certificate && isAdmin) ? (
                              <Button size="sm" variant="outline" className="h-7 px-2 border-[#2BBAA4]/40 text-[#2BBAA4] hover:bg-[#2BBAA4]/10 text-xs font-normal"
                                disabled={generatingId === t.enrollment_id}
                                onClick={() => handleGenerate(t)}
                                data-testid={`rut-generate-cert-${t.enrollment_id}`}>
                                <Certificate size={14} className="mr-1" />
                                {generatingId === t.enrollment_id ? 'Generando...' : 'Generar'}
                              </Button>
                            ) : (
                              <span className="text-[#2B4B8C]/30 text-xs">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-[#2B4B8C]/40 py-4 text-center">Sin capacitaciones registradas</p>
              )}
            </div>
          </div>
        )}

        {searched && !result && !loading && (
          <p className="text-sm text-[#2B4B8C]/40 text-center py-4">No se encontró ningún trabajador con ese RUT</p>
        )}
      </CardContent>

      {/* Certificate Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-white overflow-hidden" data-testid="rut-certificate-modal">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#2B4B8C]/10">
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
              <Certificate size={20} className="text-[#2BBAA4]" /> Certificado
              {previewLabel ? <span className="text-[#2B4B8C]/50 font-normal text-sm">— {previewLabel}</span> : null}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-[#525659] h-[75vh] flex items-center justify-center">
            {previewLoading ? (
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2BBAA4]" />
            ) : previewUrl ? (
              <iframe src={previewUrl} title="Vista previa del certificado" className="w-full h-full border-0" data-testid="rut-certificate-iframe" />
            ) : (
              <p className="text-white/70 text-sm">No se pudo cargar el certificado</p>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#2B4B8C]/10">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[#2B4B8C]/20 text-[#2B4B8C]">Cerrar</Button>
            <Button onClick={handleDownload} className="bg-[#2BBAA4] hover:bg-[#249b89] text-white">
              <Download size={16} className="mr-1.5" /> Descargar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
